/**
 * Hook for calculation service operations
 */

import { useState, useEffect } from 'react';
import { FirestoreService } from '@/services/firestore';
import { useAuth } from '@/hooks/useAuth';
import type { CalculationInput } from '@/types/calculation';

const calculationService = new FirestoreService<CalculationInput>(
  'calculations'
);

export const useCalculationService = () => {
  const { isAuthenticated, isApproved, loading: authLoading } = useAuth();
  const [calculations, setCalculations] = useState<CalculationInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCalculations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await calculationService.getAll();
      setCalculations(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error loading calculations'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load data when authentication is complete and user is approved
    if (!authLoading && isAuthenticated && isApproved) {
      loadCalculations();
    } else if (!authLoading && (!isAuthenticated || !isApproved)) {
      // Reset state if user is not authenticated or approved
      setCalculations([]);
      setLoading(false);
      setError(null);
    }
  }, [authLoading, isAuthenticated, isApproved]);

  return {
    calculations,
    loading,
    error,
    refetch: loadCalculations,
  };
};
